import { getStore } from '../create'
import { api, isLoggedIn } from '../api'
import { AIFansSubscription, SubscriptionEvent } from '/common/types/aifans-schema'

export const aifansSubscriptionApi = {
  cancelAIFansAdminSubscription,
  createAIFansSubscription,
  createPlatformMembership,
  cancelPlatformMembership,
  createTokenReloadLink,
  getSubscriptions,
  getTransactions,
  createCancellationLink,
  createReactivationLink,
  reactivateAIFansSubscription,
  reactivateAdminSubscription,
  sendTip,
  getAdminTransactions,
  getAdminSubscriptions,
  getSubscriptionEvents,
}

export async function getSubscriptions() {
  const res = await api.get<AIFansSubscription[]>('/aifans-subscription/')
  return res
}

export async function getTransactions(page: number, pageSize: number) {
  const res = await api.get<{ transactions: SubscriptionEvent[]; totalCount: number }>(
    '/aifans-subscription/transactions',
    { page, pageSize }
  )
  return res
}

export async function createAIFansSubscription(characterId: string) {
  const res = await api.post<{ redirectURL: string }>(`/aifans-subscription/${characterId}`)

  if (res.error) {
    throw res.error
  }

  return res
}

export async function cancelAIFansAdminSubscription(
  characterId: string,
  userId: string,
  reason: string
) {
  const res = await api.post<{ success: boolean; output: string }>(
    `/aifans-subscription/admin/${characterId}/cancel`,
    { userId, reason }
  )

  if (res.error) {
    throw res.error
  }

  return res
}

export async function createTokenReloadLink(
  amount: string,
  tokenPlanId: number,
  tokenAmount: number
) {
  const res = await api.post<{ redirectURL: string }>('/aifans-subscription/tokenreload', {
    amount,
    tokenPlanId,
    tokenAmount,
  })

  if (res.error) {
    throw res.error
  }

  return res
}

export async function createCancellationLink(characterId: string, reason: string) {
  const res = await api.post<{ success: boolean }>(`/aifans-subscription/${characterId}/cancel`, {
    reason,
  })

  if (res.error) {
    throw res.error
  }

  return res
}

export async function createReactivationLink(characterId: string) {
  const res = await api.post<{ redirectURL: string }>(
    `/aifans-subscription/${characterId}/reactivate-link`
  )

  if (res.error) {
    throw res.error
  }

  return res
}

interface PaymentDetails {
  amount: number
  currency: string
  paymentType: string
  paymentSubtype: string
}

export async function reactivateAIFansSubscription(
  characterId: string,
  paymentDetails: PaymentDetails,
  validationParams: Record<string, string>
) {
  const res = await api.post<{ success: boolean }>(
    `/aifans-subscription/${characterId}/reactivate`,
    {
      paymentDetails,
      validationParams,
    }
  )

  if (res.error) {
    throw res.error
  }

  return res
}

export async function reactivateAdminSubscription(characterId: string, userId: string) {
  const res = await api.post<{ success: boolean }>(
    `/aifans-subscription/admin/${characterId}/reactivate`,
    {
      userId,
      paymentDetails: {
        amount: 0,
        currency: 'USD',
        paymentType: 'admin',
        paymentSubtype: 'admin-reactivation',
      },
    }
  )

  if (res.error) {
    throw res.error
  }

  return res
}

export async function sendTip(imageId: string, amount: number, characterId: string) {
  const res = await api.post<{ success: boolean }>(`/aifans-subscription/tip/${imageId}`, {
    amount,
    characterId,
  })

  if (res.error) {
    throw res.error
  }

  return res
}

export async function getAdminTransactions(page: number, pageSize: number) {
  const res = await api.get<{ transactions: SubscriptionEvent[]; totalCount: number }>(
    '/aifans-subscription/admin/transactions',
    { page, pageSize }
  )
  return res
}

export async function getAdminSubscriptions(page: number, pageSize: number) {
  const res = await api.get<{ subscriptions: AIFansSubscription[]; totalCount: number }>(
    '/aifans-subscription/admin/subscriptions',
    { page, pageSize }
  )
  return res
}

export async function getSubscriptionEvents(subscriptionId: string) {
  const res = await api.get<{ result: SubscriptionEvent[] }>(
    `/aifans-subscription/admin/subscription/${subscriptionId}/events`
  )
  return res
}

export async function createPlatformMembership(planId: string) {
  const res = await api.post<{ redirectURL: string }>('/aifans-subscription/membership', { planId })
  if (res.error) {
    throw res.error
  }
  return res
}

export async function cancelPlatformMembership(reason: string) {
  const res = await api.post<{ success: boolean }>('/aifans-subscription/membership/cancel', {
    reason,
  })
  if (res.error) {
    throw res.error
  }
  return res
}
